import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  trackDirectionsClick,
  trackEmailClick,
  trackFormSubmit,
  trackPageView,
  trackPhoneClick,
  trackWhatsAppConversion,
} from "@/utils/googleAnalytics";

/**
 * Rastreamento global do site:
 *  - envia uma visualização de página a cada troca de rota (site é SPA);
 *  - captura cliques em telefone, e-mail, mapa e WhatsApp em qualquer lugar
 *    da página, inclusive nos botões do rodapé e do cabeçalho.
 */
export default function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      trackPageView(`${location.pathname}${location.search}`, document.title);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest?.("a[href], [data-whatsapp-cta]") as HTMLElement | null;
      if (!el) {
        return;
      }

      const href = (el.getAttribute("href") ?? "").trim();
      const lower = href.toLowerCase();

      if (lower.startsWith("tel:")) {
        const phone = href.slice(4).replace(/[^\d+]/g, "");
        const place = el.closest("footer")
          ? "rodape"
          : el.closest("header") || el.closest("nav")
            ? "cabecalho"
            : "conteudo";
        trackPhoneClick(phone, place);
        return;
      }

      if (lower.startsWith("mailto:")) {
        trackEmailClick(href.slice(7).split("?")[0], "site");
        return;
      }

      if (lower.indexOf("google.com/maps") !== -1 || lower.indexOf("maps.app.goo.gl") !== -1) {
        trackDirectionsClick("site");
        return;
      }

      const isWhatsApp =
        el.hasAttribute("data-whatsapp-cta") ||
        lower.indexOf("wa.me") !== -1 ||
        lower.indexOf("whatsapp") !== -1;

      if (isWhatsApp) {
        // Disparo imediato (síncrono) para o Google receber o evento antes de
        // o navegador abrir a aba do WhatsApp.
        trackWhatsAppConversion();
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  useEffect(() => {
    const handleSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement | null;
      if (!form || !form.hasAttribute("data-readdy-form")) {
        return;
      }
      trackFormSubmit(form.getAttribute("name") || form.id || "formulario");
    };

    document.addEventListener("submit", handleSubmit, true);
    return () => document.removeEventListener("submit", handleSubmit, true);
  }, []);

  return null;
}